import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Input,
  VStack,
  HStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Heading,
  useToast,
  Container,
  Text,
  FormControl,
  FormLabel,
  Badge,
  Divider,
  Card,
  CardHeader,
  CardBody,
  Flex,
  Avatar,
  InputGroup,
  InputLeftElement,
  Tooltip,
  ScaleFade,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import { 
  DeleteIcon, 
  AddIcon, 
  ViewIcon,
  StarIcon,
  TimeIcon,
  RepeatIcon,
  WarningIcon 
} from '@chakra-ui/icons';
import { MdChildCare, MdLocationOn } from 'react-icons/md';

const AddNewModule = () => {
  const [anganwadiName, setAnganwadiName] = useState('');
  const [anganwadiList, setAnganwadiList] = useState([]);
  const [isHovered, setIsHovered] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  // Load data from localStorage on component mount
  useEffect(() => {
    const savedData = localStorage.getItem('anganwadiCenters');
    if (savedData) {
      try {
        setAnganwadiList(JSON.parse(savedData));
      } catch (error) {
        console.error('Error loading saved data:', error);
      }
    }
    setIsLoading(false);
  }, []);

  // Save data to localStorage whenever anganwadiList changes
  useEffect(() => {
    if (!isLoading) {
      localStorage.setItem('anganwadiCenters', JSON.stringify(anganwadiList));
    }
  }, [anganwadiList, isLoading]);

  const handleAddAnganwadi = () => {
    if (!anganwadiName.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter an Anganwadi center name',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    if (anganwadiList.some(item => item.name.toLowerCase() === anganwadiName.trim().toLowerCase())) {
      toast({
        title: 'Error',
        description: 'This Anganwadi center already exists',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    const newAnganwadi = {
      id: Date.now(),
      name: anganwadiName.trim(),
      dateAdded: new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }),
      status: 'Active'
    };

    setAnganwadiList([...anganwadiList, newAnganwadi]);
    setAnganwadiName('');
    
    toast({
      title: 'Success',
      description: 'Anganwadi center added successfully',
      status: 'success',
      duration: 3000,
      isClosable: true,
      position: 'top-right',
    });
  };

  const handleDeleteAnganwadi = (id) => {
    setAnganwadiList(anganwadiList.filter(item => item.id !== id));
    toast({
      title: 'Deleted',
      description: 'Anganwadi center removed successfully',
      status: 'info',
      duration: 3000,
      isClosable: true,
      position: 'top-right',
    });
  };

  const handleClearAll = () => {
    setAnganwadiList([]);
    localStorage.removeItem('anganwadiCenters');
    onClose();
    toast({
      title: 'Cleared',
      description: 'All centers have been removed',
      status: 'warning',
      duration: 3000,
      isClosable: true,
      position: 'top-right',
    });
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" minH="100vh">
        <Text>Loading...</Text>
      </Flex>
    );
  }

  return (
    <Box 
      bg="linear-gradient(135deg, #ced1da 0%, #e3d1f3 100%)" 
      minH="100vh" 
      py={10}
      marginTop={'50px'}
    >
      <Container maxW="container.xl">
        <ScaleFade initialScale={0.9} in={true}>
          {/* Header Card */}
          <Card 
            mb={8} 
            overflow="hidden"
            boxShadow="2xl"
            borderRadius="2xl"
          >
            <Box 
              bg="white" 
              p={8} 
              position="relative"
              _before={{
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                bg: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
              }}
            >
              <Flex align="center" justify="space-between">
                <VStack align="start" spacing={2}>
                  <HStack spacing={2}>
                    <Badge 
                      colorScheme="purple" 
                      fontSize="sm" 
                      px={3} 
                      py={1} 
                      borderRadius="full"
                    >
                      PM-JANMAN Scheme
                    </Badge>
                  </HStack>
                  <Heading size="xl" color="gray.800">
                    Anganwadi Center Management
                  </Heading>
                  <Text color="gray.600" fontSize="lg">
                    Add and manage Anganwadi centers
                  </Text>
                </VStack>
                <Avatar
                  size="xl"
                  icon={<MdChildCare size="40px" />}
                  bg="purple.100"
                  color="purple.600"
                  boxShadow="lg"
                />
              </Flex>
            </Box>
          </Card>

          {/* Add New Center Card */}
          <Card 
            mb={8} 
            boxShadow="xl" 
            borderRadius="2xl"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            transition="all 0.3s"
            transform={isHovered ? 'translateY(-4px)' : 'none'}
          >
            <CardBody p={8}>
              <VStack spacing={6} align="stretch">
                <FormControl>
                  <FormLabel 
                    fontSize="lg" 
                    fontWeight="bold" 
                    color="gray.700"
                  >
                    <HStack spacing={2}>
                      <MdLocationOn color="#667eea" />
                      <Text>Name of Anganwadi Center</Text>
                    </HStack>
                  </FormLabel>
                  <HStack spacing={4}>
                    <InputGroup size="lg">
                      <InputLeftElement pointerEvents="none">
                        <MdChildCare color="gray.300" />
                      </InputLeftElement>
                      <Input
                        placeholder="e.g. Anganwadi Center - Wadgaon"
                        value={anganwadiName}
                        onChange={(e) => setAnganwadiName(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddAnganwadi()}
                        bg="gray.50"
                        border="2px solid"
                        borderColor="gray.200"
                        borderRadius="lg"
                        _hover={{ borderColor: "purple.300" }}
                        _focus={{ 
                          borderColor: "purple.500", 
                          boxShadow: "0 0 0 3px rgba(102, 126, 234, 0.1)"
                        }}
                        fontSize="md"
                      />
                    </InputGroup>
                    <Button
                      leftIcon={<AddIcon />}
                      colorScheme="purple"
                      onClick={handleAddAnganwadi}
                      size="lg"
                      px={8}
                      bgGradient="linear(to-r, #667eea, #764ba2)"
                      _hover={{
                        bgGradient: "linear(to-r, #764ba2, #667eea)",
                        transform: "translateY(-2px)",
                        boxShadow: "xl"
                      }}
                      transition="all 0.3s"
                    >
                      Add Anganwadi Center
                    </Button>
                  </HStack>
                </FormControl>
              </VStack>
            </CardBody>
          </Card>

          {/* Centers List Card */}
          <Card boxShadow="xl" borderRadius="2xl" overflow="hidden">
            <CardHeader bg="gray.50" py={4}>
              <Flex justify="space-between" align="center" px={2}>
                <HStack spacing={3}>
                  <ViewIcon color="purple.600" />
                  <Heading size="md" color="gray.700">
                    Anganwadi Centers List
                  </Heading>
                </HStack>
                <HStack spacing={3}>
                  {anganwadiList.length > 0 && (
                    <Button
                      size="sm"
                      colorScheme="red"
                      variant="ghost"
                      onClick={onOpen}
                      leftIcon={<WarningIcon />}
                      _hover={{ bg: "red.100" }}
                    >
                      Clear All
                    </Button>
                  )}
                  <Badge 
                    colorScheme="purple" 
                    fontSize="md" 
                    px={4} 
                    py={2} 
                    borderRadius="full"
                  >
                    Total Centers: {anganwadiList.length}
                  </Badge>
                </HStack>
              </Flex>
            </CardHeader>

            <CardBody p={0}>
              {anganwadiList.length > 0 ? (
                <Box
                  maxH="300px"
                  overflowY="auto"
                  sx={{
                    '&::-webkit-scrollbar': {
                      width: '8px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'rgba(102, 126, 234, 0.6)',
                      borderRadius: '8px',
                      '&:hover': {
                        backgroundColor: 'rgba(102, 126, 234, 0.8)',
                      },
                    },
                  }}
                >
                  <Table variant="simple" size="md">
                    <Thead 
                      bg="gray.100" 
                      position="sticky" 
                      top={0} 
                      zIndex={1}
                      boxShadow="sm"
                    >
                      <Tr>
                        <Th color="gray.600" fontSize="sm" py={4}>No.</Th>
                        <Th color="gray.600" fontSize="sm">Center Name</Th>
                        <Th color="gray.600" fontSize="sm">Date Added</Th>
                        <Th color="gray.600" fontSize="sm">Status</Th>
                        <Th color="gray.600" fontSize="sm" textAlign="center">Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {anganwadiList.map((item, index) => (
                        <Tr 
                          key={item.id}
                          _hover={{ bg: "purple.50", cursor: "pointer" }}
                          transition="all 0.2s"
                        >
                          <Td fontWeight="bold" color="purple.600">#{index + 1}</Td>
                          <Td>
                            <HStack spacing={2}>
                              <StarIcon color="yellow.400" boxSize={3} />
                              <Text fontWeight="medium">{item.name}</Text>
                            </HStack>
                          </Td>
                          <Td>
                            <HStack spacing={2}>
                              <TimeIcon color="gray.400" boxSize={3} />
                              <Text color="gray.600">{item.dateAdded}</Text>
                            </HStack>
                          </Td>
                          <Td>
                            <Badge 
                              colorScheme="green" 
                              px={3} 
                              py={1} 
                              borderRadius="full"
                            >
                              {item.status}
                            </Badge>
                          </Td>
                          <Td textAlign="center">
                            <Tooltip label="Delete Center" hasArrow>
                              <IconButton
                                icon={<DeleteIcon />}
                                colorScheme="red"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteAnganwadi(item.id)}
                                aria-label="Delete center"
                                _hover={{ 
                                  bg: "red.100", 
                                  transform: "scale(1.1)",
                                  color: "red.600"
                                }}
                                transition="all 0.2s"
                              />
                            </Tooltip>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              ) : (
                <Flex 
                  direction="column" 
                  align="center" 
                  justify="center" 
                  py={16}
                  px={4}
                >
                  <Box 
                    bg="purple.50" 
                    p={6} 
                    borderRadius="full" 
                    mb={4}
                  >
                    <MdChildCare size="48px" color="#667eea" />
                  </Box>
                  <Text color="gray.500" fontSize="xl" mb={2}>
                    No Anganwadi Centers Found
                  </Text>
                  <Text color="gray.400" textAlign="center">
                    Add your first Anganwadi center using the form above
                  </Text>
                </Flex>
              )}
            </CardBody>
          </Card>

          {/* Clear All Confirmation Modal */}
          <Modal isOpen={isOpen} onClose={onClose} isCentered>
            <ModalOverlay 
              bg='blackAlpha.300'
              backdropFilter='blur(10px)'
            />
            <ModalContent borderRadius="2xl">
              <ModalHeader bg="red.50" borderTopRadius="2xl">
                <HStack spacing={2}>
                  <WarningIcon color="red.500" />
                  <Text color="red.700">Clear All Centers</Text>
                </HStack>
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody py={6}>
                <VStack spacing={3} align="stretch">
                  <Text fontSize="lg" fontWeight="medium">
                    Are you sure you want to delete all centers?
                  </Text>
                  <Text color="gray.600">
                    This action will permanently remove all {anganwadiList.length} centers from the list. This cannot be undone.
                  </Text>
                  <Box bg="red.50" p={3} borderRadius="lg" mt={2}>
                    <Text color="red.600" fontSize="sm">
                      ⚠️ Warning: This action is irreversible
                    </Text>
                  </Box>
                </VStack>
              </ModalBody>
              <ModalFooter bg="gray.50" borderBottomRadius="2xl">
                <Button variant="ghost" mr={3} onClick={onClose}>
                  Cancel
                </Button>
                <Button 
                  colorScheme="red" 
                  onClick={handleClearAll}
                  leftIcon={<DeleteIcon />}
                >
                  Delete All
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </ScaleFade>
      </Container>
    </Box>
  );
};

export default AddNewModule;